/**
 * The permanent paper-bankroll ledger for the BTC 15m strategies.
 *
 * Every tracked history (V1, V1 + take-profit, V2/V3/V4, coin flip, dice)
 * keeps only its most recent 1,200 rounds, about 12.5 days. Replaying a $200
 * bankroll over that window gave a figure that quietly restarted from $200
 * at a new point every time an old round dropped off, and once a bankroll
 * was nearly gone its losses stopped counting because it could no longer
 * place a bet.
 *
 * The ledger fixes both. Each finished round is folded into running totals
 * exactly once, in close-time order, and those totals are never
 * recalculated, so nothing shifts when the window slides. A bankroll too
 * small to place the next bet ends its run (a bust): it is topped back up
 * to the starting pool, the bet is placed, and the next run begins. Runs
 * are numbered, every bust is counted, and the lifetime totals run straight
 * through all of them, so no bet a strategy wants is ever left out.
 *
 * Pure logic: no network, no storage. Paper only; nothing here is executed.
 */
import { z } from 'zod';
import {
  DEFAULT_CONFIG,
  KALSHI_TAKER_FEES,
  calculateAllocation,
  earlyExitTradePnl,
  heldTradePnl,
  openFirstRun,
  recordRunBet,
  stakeForBet,
  tradeFees,
  type BankrollRun,
  type FeeModel,
  type Recommendation,
} from '@whaletail/signal-engine';
import type { Btc15History } from './btc15History';
import type { ModelHistory } from './btc15ModelHistory';
import type { TakeProfitHistory } from './btc15TakeProfitHistory';
import type { StrategyKey } from './modelNames';

export const LEDGER_SCHEMA_VERSION = 1;

export const LEDGER_STRATEGIES: readonly StrategyKey[] = [
  'v1',
  'v1-tp',
  'v2',
  'v3',
  'v4',
  'coinflip',
  'dice',
];

export interface LedgerRules {
  /** What every run starts with, and what a bust is topped back up to. */
  startingPool: number;
  /**
   * Whether a bankroll too small to place a bet is topped back up (ending
   * the run) so the bet can be placed. Always on in the live ledger. Off, a
   * bet the bankroll can't cover is simply missed, which is exactly what the
   * old window replays did; the tests use that to prove the two agree.
   */
  topUp: boolean;
  /**
   * How long a round waits for Kalshi's official result before the ledger
   * accepts the provisional Coinbase-proxy grade (or, if it never settled at
   * all, leaves it out). Rounds are folded strictly in order, so one
   * waiting round holds back the ones after it for at most this long.
   */
  graceMs: number;
  /**
   * A round with no official result is also final once Kalshi has published
   * the official result of a round that closed at least this much later.
   * Kalshi has then moved on and no result is coming: its weekly maintenance
   * window (Thursdays, 3 to 5 AM Eastern) has no markets at all, and without
   * this every round tracked in it would hold the ledger back for the whole
   * grace period.
   */
  passedByMs: number;
}

/**
 * Fixed in advance, like a forward test's pass bar.
 *
 * A run ends the first time its bankroll can't cover a bet the strategy
 * wants at the sizing rule's $1 minimum. There is deliberately no fixed
 * dollar line: quarter-Kelly on a thin edge needs $40 to $70 of bankroll to
 * reach $1, so a line at, say, $20 would leave a losing thin-edge strategy
 * parked above it for ever, placing nothing, with its loss frozen.
 */
export const LEDGER_RULES: LedgerRules = {
  startingPool: DEFAULT_CONFIG.simulatedStartingPool,
  topUp: true,
  graceMs: 24 * 60 * 60 * 1000,
  passedByMs: 60 * 60 * 1000,
};

/** How many recent bankroll points and finished runs a ledger keeps for display. */
const MAX_SERIES_POINTS = 240;
const MAX_STORED_RUNS = 60;

/** One finished round, reduced to what the bankroll replay needs. */
export interface LedgerRound {
  roundId: string;
  closeMs: number;
  /** What the sizing rule is asked to buy: the strategy's side, or SKIP. */
  recommendation: Recommendation;
  probability: number;
  /** The ask recorded at entry, or null when there was no quote. */
  entry: number | null;
  /** The bid a take-profit position was sold at, or null when held to settlement. */
  exit: number | null;
  /** Whether the call was right (for take-profit: whether the position closed at a gain). */
  won: boolean;
}

export type LedgerRun = BankrollRun;

/** [round close time, bankroll after that bet, run number]. */
export type LedgerPoint = [number, number, number];

export interface StrategyLedger {
  /** Close time of the last round examined; later rounds are still to come. */
  cursorMs: number;
  /** Close time of the first round counted. */
  sinceMs: number | null;
  /** Every settled call, bet on or not: how often the side was right. */
  calls: number;
  right: number;
  /** Money across every run. */
  bets: number;
  wins: number;
  staked: number;
  fees: number;
  pnl: number;
  /** Paid in: the first bankroll plus every top-up after a bust. */
  deposited: number;
  /**
   * One contract on every bet a full bankroll would place, whatever the
   * bankroll is at the time: the measure of the strategy that sizing and
   * busts can't touch. Sum and sum of squares of net per contract (dollars).
   * With top-ups on, these are the same bets as `bets`.
   */
  flatBets: number;
  flatWins: number;
  flatNet: number;
  flatNetSq: number;
  /** Runs ended by a bust, including any no longer kept in `runs`. */
  busts: number;
  /** Oldest first; the last one is the current run. */
  runs: LedgerRun[];
  series: LedgerPoint[];
}

export type BankrollLedger = {
  schemaVersion: number;
  updatedMs: number | null;
  strategies: Record<StrategyKey, StrategyLedger>;
};

export function emptyStrategyLedger(): StrategyLedger {
  return {
    cursorMs: 0,
    sinceMs: null,
    calls: 0,
    right: 0,
    bets: 0,
    wins: 0,
    staked: 0,
    fees: 0,
    pnl: 0,
    deposited: 0,
    flatBets: 0,
    flatWins: 0,
    flatNet: 0,
    flatNetSq: 0,
    busts: 0,
    runs: [],
    series: [],
  };
}

export function emptyBankrollLedger(): BankrollLedger {
  return {
    schemaVersion: LEDGER_SCHEMA_VERSION,
    updatedMs: null,
    strategies: Object.fromEntries(
      LEDGER_STRATEGIES.map((key) => [key, emptyStrategyLedger()]),
    ) as Record<StrategyKey, StrategyLedger>,
  };
}

/** Net of one contract after fees: held to settlement, or sold early at `exit`. */
function netPerContract(
  entry: number,
  exit: number | null,
  won: boolean,
  fees: FeeModel,
): number {
  const cost = entry + fees.perContract(entry);
  return exit !== null ? exit - fees.perContract(exit) - cost : (won ? 1 : 0) - cost;
}

/**
 * Folds rounds into a strategy's ledger. `rounds` must be in close-time
 * order and all later than the ledger's cursor; earlier ones are ignored, so
 * folding the same rounds twice changes nothing. Returns a new ledger.
 */
export function foldRounds(
  ledger: StrategyLedger,
  rounds: readonly LedgerRound[],
  rules: LedgerRules = LEDGER_RULES,
  fees: FeeModel = KALSHI_TAKER_FEES,
): StrategyLedger {
  const next: StrategyLedger = {
    ...ledger,
    runs: ledger.runs.map((run) => ({ ...run })),
    series: [...ledger.series],
  };

  for (const round of rounds) {
    if (round.closeMs <= next.cursorMs) continue;
    next.cursorMs = round.closeMs;
    if (next.sinceMs === null) next.sinceMs = round.closeMs;
    openFirstRun(next, round.closeMs, rules.startingPool);

    next.calls++;
    if (round.won) next.right++;

    // Would a full bankroll bet this round at all? (SKIP, no quote and a
    // too-thin edge all size to $0.)
    const fullStake = calculateAllocation(
      round.recommendation,
      round.probability,
      round.entry,
      rules.startingPool,
    ).stake;
    if (fullStake <= 0 || round.entry === null) continue;
    const entry = round.entry;

    const flat = netPerContract(entry, round.exit, round.won, fees);
    next.flatBets++;
    if (flat > 0) next.flatWins++;
    next.flatNet += flat;
    next.flatNetSq += flat * flat;

    const { stake, run } = stakeForBet(
      next,
      round.closeMs,
      rules.startingPool,
      (bankroll) =>
        calculateAllocation(round.recommendation, round.probability, entry, bankroll)
          .stake,
      rules.topUp,
    );
    // The bankroll can't cover the $1 minimum and top-ups are off: missed.
    if (stake <= 0) continue;

    const tradePnl =
      round.exit !== null
        ? earlyExitTradePnl(stake, entry, round.exit, fees)
        : heldTradePnl(stake, entry, round.won, fees);
    next.bets++;
    next.staked += stake;
    next.fees += tradeFees(stake, entry, round.exit, fees);
    next.pnl += tradePnl;
    if (round.won) next.wins++;

    recordRunBet(run, tradePnl, round.won);
    next.series.push([round.closeMs, run.end, run.n]);
  }

  if (next.series.length > MAX_SERIES_POINTS) {
    next.series = next.series.slice(-MAX_SERIES_POINTS);
  }
  if (next.runs.length > MAX_STORED_RUNS) {
    next.runs = next.runs.slice(-MAX_STORED_RUNS);
  }
  return next;
}

interface SettleableLike {
  roundId: string;
  roundCloseMs: number;
  settled: boolean;
  won: boolean | null;
  settlementSource?: 'kalshi_official' | 'coinbase_proxy';
}

export interface FinalRounds {
  /** Rounds ready to fold, in close-time order. */
  rounds: LedgerRound[];
  /**
   * Rounds past the grace period that never settled, and so are left out
   * for good. Counted so a gap in the ledger is explained, not silent.
   */
  dropped: number;
  /**
   * Close time of the last round examined and decided (folded or left
   * out), or null if none was. The cursor moves here, so a round that was
   * left out is never looked at again.
   */
  throughMs: number | null;
  /** True when an earlier round is still waiting for its result. */
  waiting: boolean;
}

/**
 * Picks the rounds after `cursorMs` whose outcome can no longer change, in
 * close-time order, stopping at the first one that might: a round still
 * open, unsettled, or graded only by the Coinbase proxy, that is inside the
 * grace period and that Kalshi has not yet passed by (see
 * `LedgerRules.passedByMs`). Stopping (rather than skipping ahead) keeps the
 * fold in strict time order, which the compounding bankroll depends on.
 */
function finalRounds<T extends SettleableLike>(
  records: readonly T[],
  cursorMs: number,
  nowMs: number,
  graceMs: number,
  passedByMs: number,
  toRound: (record: T & { won: boolean }) => LedgerRound,
  lockedEarly: (record: T) => boolean = () => false,
): FinalRounds {
  // The latest round Kalshi has published an official result for.
  let latestOfficialMs = -Infinity;
  for (const r of records) {
    if (r.settled && r.settlementSource === 'kalshi_official') {
      latestOfficialMs = Math.max(latestOfficialMs, r.roundCloseMs);
    }
  }
  const pending = records
    .filter((r) => r.roundCloseMs > cursorMs && r.roundCloseMs <= nowMs)
    .sort((a, b) => a.roundCloseMs - b.roundCloseMs);
  const rounds: LedgerRound[] = [];
  let dropped = 0;
  let throughMs: number | null = null;
  for (const record of pending) {
    // No official result is coming: the grace period is over, or Kalshi
    // has already published a round that closed well after this one.
    const pastGrace =
      nowMs - record.roundCloseMs > graceMs ||
      latestOfficialMs - record.roundCloseMs >= passedByMs;
    const settled = record.settled && record.won !== null;
    if (!settled) {
      if (!pastGrace) return { rounds, dropped, throughMs, waiting: true };
      // Never settled: left out, and the cursor moves past it.
      dropped++;
      throughMs = record.roundCloseMs;
      continue;
    }
    const final =
      lockedEarly(record) || record.settlementSource === 'kalshi_official' || pastGrace;
    if (!final) return { rounds, dropped, throughMs, waiting: true };
    rounds.push(toRound(record as T & { won: boolean }));
    throughMs = record.roundCloseMs;
  }
  return { rounds, dropped, throughMs, waiting: false };
}

/**
 * Moves a ledger's cursor up to `throughMs` (never back). Used after a fold
 * so rounds that were examined and left out are not examined again.
 */
export function advanceCursor(
  ledger: StrategyLedger,
  throughMs: number | null,
): StrategyLedger {
  if (throughMs === null || throughMs <= ledger.cursorMs) return ledger;
  return { ...ledger, cursorMs: throughMs };
}

export interface FinalRoundOptions {
  /** Close time of the last round already folded. */
  cursorMs?: number;
  nowMs?: number;
  /** Override the grace period (0 treats every settled round as final). */
  graceMs?: number;
  passedByMs?: number;
}

function options(o: FinalRoundOptions): Required<FinalRoundOptions> {
  return {
    cursorMs: o.cursorMs ?? 0,
    nowMs: o.nowMs ?? Date.now(),
    graceMs: o.graceMs ?? LEDGER_RULES.graceMs,
    passedByMs: o.passedByMs ?? LEDGER_RULES.passedByMs,
  };
}

/** V1: sized on its gated recommendation, so a SKIP never bets. */
export function finalV1Rounds(
  history: Btc15History,
  o: FinalRoundOptions = {},
): FinalRounds {
  const { cursorMs, nowMs, graceMs, passedByMs } = options(o);
  return finalRounds(history.predictions, cursorMs, nowMs, graceMs, passedByMs, (p) => ({
    roundId: p.roundId,
    closeMs: p.roundCloseMs,
    recommendation: p.recommendation,
    probability: p.modelProbability,
    entry: p.contractEntryPrice,
    exit: null,
    won: p.won,
  }));
}

/** V2/V3/V4 and the baselines: every round is a candidate; the edge gate decides. */
export function finalModelRounds(
  history: ModelHistory,
  o: FinalRoundOptions = {},
): FinalRounds {
  const { cursorMs, nowMs, graceMs, passedByMs } = options(o);
  return finalRounds(history.predictions, cursorMs, nowMs, graceMs, passedByMs, (p) => ({
    roundId: p.roundId,
    closeMs: p.roundCloseMs,
    recommendation: p.action,
    probability: p.probability,
    entry: p.contractEntryPrice,
    exit: null,
    won: p.won,
  }));
}

/** V1 + take-profit: an early sale is final the moment it happens. */
export function finalTakeProfitRounds(
  history: TakeProfitHistory,
  o: FinalRoundOptions = {},
): FinalRounds {
  const { cursorMs, nowMs, graceMs, passedByMs } = options(o);
  return finalRounds(
    history.predictions,
    cursorMs,
    nowMs,
    graceMs,
    passedByMs,
    (p) => ({
      roundId: p.roundId,
      closeMs: p.roundCloseMs,
      recommendation: p.action,
      probability: p.probability,
      entry: p.contractEntryPrice,
      exit: p.exitedEarly && p.exitPrice !== null ? p.exitPrice : null,
      won: p.won,
    }),
    (p) => p.exitedEarly,
  );
}

export interface StrategySummary {
  sinceMs: number | null;
  throughMs: number | null;
  calls: number;
  /** Share of settled calls that were right. */
  hitRate: number | null;
  bets: number;
  wins: number;
  losses: number;
  staked: number;
  fees: number;
  /** Lifetime P&L after fees, across every run. */
  pnl: number;
  /** Per dollar wagered. */
  roi: number | null;
  deposited: number;
  bankroll: number;
  runCount: number;
  busts: number;
  current: LedgerRun | null;
  /** Finished runs, newest first. */
  past: LedgerRun[];
  flat: {
    bets: number;
    wins: number;
    /** Average net per contract after fees (dollars). */
    netPerContract: number | null;
    /** ~95% range (dollars). */
    netLow: number | null;
    netHigh: number | null;
  };
  series: LedgerPoint[];
}

/** The figures a scorecard shows, from a ledger's running totals. */
export function summarizeStrategy(ledger: StrategyLedger): StrategySummary {
  const current = ledger.runs.length ? ledger.runs[ledger.runs.length - 1] : null;
  const n = ledger.flatBets;
  const mean = n ? ledger.flatNet / n : null;
  const variance =
    n > 1 && mean !== null
      ? Math.max(0, (ledger.flatNetSq - n * mean * mean) / (n - 1))
      : null;
  const se = variance !== null ? Math.sqrt(variance / n) : null;
  return {
    sinceMs: ledger.sinceMs,
    throughMs: ledger.cursorMs || null,
    calls: ledger.calls,
    hitRate: ledger.calls ? ledger.right / ledger.calls : null,
    bets: ledger.bets,
    wins: ledger.wins,
    losses: ledger.bets - ledger.wins,
    staked: ledger.staked,
    fees: ledger.fees,
    pnl: ledger.pnl,
    roi: ledger.staked ? ledger.pnl / ledger.staked : null,
    deposited: ledger.deposited,
    bankroll: current ? current.end : 0,
    runCount: current ? current.n : 0,
    busts: ledger.busts,
    current,
    past: ledger.runs.filter((run) => run.endedMs !== null).reverse(),
    flat: {
      bets: n,
      wins: ledger.flatWins,
      netPerContract: mean,
      netLow: mean !== null && se !== null ? mean - 1.96 * se : null,
      netHigh: mean !== null && se !== null ? mean + 1.96 * se : null,
    },
    series: ledger.series,
  };
}

export interface LedgerHistories {
  v1: Btc15History;
  takeProfit: TakeProfitHistory;
  models: Partial<Record<'v2' | 'v3' | 'v4' | 'coinflip' | 'dice', ModelHistory>>;
}

/** The final rounds each strategy has after its own cursor. */
export function finalRoundsByStrategy(
  histories: LedgerHistories,
  cursors: Record<StrategyKey, number>,
  o: Omit<FinalRoundOptions, 'cursorMs'> = {},
): Record<StrategyKey, FinalRounds> {
  const model = (key: 'v2' | 'v3' | 'v4' | 'coinflip' | 'dice'): FinalRounds => {
    const history = histories.models[key];
    return history
      ? finalModelRounds(history, { ...o, cursorMs: cursors[key] })
      : { rounds: [], dropped: 0, throughMs: null, waiting: false };
  };
  return {
    v1: finalV1Rounds(histories.v1, { ...o, cursorMs: cursors.v1 }),
    'v1-tp': finalTakeProfitRounds(histories.takeProfit, {
      ...o,
      cursorMs: cursors['v1-tp'],
    }),
    v2: model('v2'),
    v3: model('v3'),
    v4: model('v4'),
    coinflip: model('coinflip'),
    dice: model('dice'),
  };
}

/**
 * A ledger built from the stored histories alone, treating every settled
 * round as final. This is what the page shows until the server's permanent
 * ledger has data: the same rules and arithmetic, but only as far back as
 * the 1,200-round window reaches.
 */
export function ledgerFromHistories(
  histories: LedgerHistories,
  nowMs: number = Date.now(),
): BankrollLedger {
  const ledger = emptyBankrollLedger();
  const cursors = Object.fromEntries(LEDGER_STRATEGIES.map((key) => [key, 0])) as Record<
    StrategyKey,
    number
  >;
  const final = finalRoundsByStrategy(histories, cursors, { nowMs, graceMs: 0 });
  for (const key of LEDGER_STRATEGIES) {
    ledger.strategies[key] = foldRounds(ledger.strategies[key], final[key].rounds);
  }
  return ledger;
}

const runSchema = z.object({
  n: z.number(),
  startedMs: z.number(),
  endedMs: z.number().nullable(),
  start: z.number(),
  end: z.number(),
  peak: z.number(),
  low: z.number(),
  bets: z.number(),
  wins: z.number(),
});

const strategyLedgerSchema = z.object({
  cursorMs: z.number(),
  sinceMs: z.number().nullable(),
  calls: z.number(),
  right: z.number(),
  bets: z.number(),
  wins: z.number(),
  staked: z.number(),
  fees: z.number(),
  pnl: z.number(),
  deposited: z.number(),
  flatBets: z.number(),
  flatWins: z.number(),
  flatNet: z.number(),
  flatNetSq: z.number(),
  busts: z.number(),
  runs: z.array(runSchema),
  series: z.array(z.tuple([z.number(), z.number(), z.number()])),
});

const bankrollLedgerSchema = z.object({
  schemaVersion: z.number(),
  updatedMs: z.number().nullable(),
  strategies: z.object({
    v1: strategyLedgerSchema,
    'v1-tp': strategyLedgerSchema,
    v2: strategyLedgerSchema,
    v3: strategyLedgerSchema,
    v4: strategyLedgerSchema,
    coinflip: strategyLedgerSchema,
    dice: strategyLedgerSchema,
  }),
});

/**
 * Parses a stored ledger. Throws on anything unexpected rather than
 * returning an empty ledger: these totals can't be rebuilt from the
 * 1,200-round histories, so a caller must never mistake a damaged blob for
 * "no ledger yet" and write a fresh one over it.
 */
export function parseBankrollLedger(raw: string): BankrollLedger {
  const parsed = bankrollLedgerSchema.parse(JSON.parse(raw));
  if (parsed.schemaVersion !== LEDGER_SCHEMA_VERSION) {
    throw new Error(`Unknown bankroll ledger schema version ${parsed.schemaVersion}`);
  }
  return parsed;
}

/**
 * A folded round as kept in the permanent archive, so the ledger can be
 * rebuilt if a rule ever changes:
 * [roundId, closeMs, recommendation, probability, entry, exit, won].
 */
export type ArchivedRound = [
  string,
  number,
  Recommendation,
  number,
  number | null,
  number | null,
  0 | 1,
];

/** The rounds one run folded, per strategy. Written once and never changed. */
export type ArchiveBatch = Partial<Record<StrategyKey, ArchivedRound[]>>;

const archivedRoundSchema = z.tuple([
  z.string(),
  z.number(),
  z.enum(['UP', 'DOWN', 'SKIP']),
  z.number(),
  z.number().nullable(),
  z.number().nullable(),
  z.union([z.literal(0), z.literal(1)]),
]);

const archiveBatchSchema = z
  .object({
    v1: z.array(archivedRoundSchema),
    'v1-tp': z.array(archivedRoundSchema),
    v2: z.array(archivedRoundSchema),
    v3: z.array(archivedRoundSchema),
    v4: z.array(archivedRoundSchema),
    coinflip: z.array(archivedRoundSchema),
    dice: z.array(archivedRoundSchema),
  })
  .partial();

/** Throws on a damaged batch rather than reading it as empty. */
export function parseArchiveBatch(raw: string): ArchiveBatch {
  return archiveBatchSchema.parse(JSON.parse(raw));
}

export function toArchived(round: LedgerRound): ArchivedRound {
  return [
    round.roundId,
    round.closeMs,
    round.recommendation,
    round.probability,
    round.entry,
    round.exit,
    round.won ? 1 : 0,
  ];
}

export function fromArchived(row: ArchivedRound): LedgerRound {
  return {
    roundId: row[0],
    closeMs: row[1],
    recommendation: row[2],
    probability: row[3],
    entry: row[4],
    exit: row[5],
    won: row[6] === 1,
  };
}

/**
 * Where a run's batch is stored: under the UTC day and time the run
 * happened, "rounds/2026-10-08/20-07-13.json". Keys sort in time order.
 */
export function archiveBatchKey(runMs: number): string {
  const iso = new Date(runMs).toISOString();
  return `rounds/${iso.slice(0, 10)}/${iso.slice(11, 19).replaceAll(':', '-')}.json`;
}

/**
 * Rebuilds every strategy's ledger from archive batches given in key order.
 * A round can appear in two batches: a run that archived its rounds and then
 * failed to save the ledger folds them again next time. The last copy is the
 * one that counts, because the run that archived it last is the one whose
 * fold the saved ledger holds.
 */
export function ledgerFromArchive(
  batches: readonly ArchiveBatch[],
  rules: LedgerRules = LEDGER_RULES,
): BankrollLedger {
  const ledger = emptyBankrollLedger();
  for (const key of LEDGER_STRATEGIES) {
    const byRound = new Map<string, LedgerRound>();
    for (const batch of batches) {
      for (const row of batch[key] ?? []) byRound.set(row[0], fromArchived(row));
    }
    const rounds = [...byRound.values()].sort((a, b) => a.closeMs - b.closeMs);
    ledger.strategies[key] = foldRounds(ledger.strategies[key], rounds, rules);
  }
  return ledger;
}
